import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { INDICATOR_TYPE_STRUCTURAL } from '../../../utils/const';

export const selectFilterOptionsWithDisabled = createSelector(
    (state: RootState) => state.open_data_filters.filterOptions,
    (state: RootState) => state.open_data_filters.derechos,
    (state: RootState) => state.open_data_filters.dimensiones,
    (state: RootState) => state.open_data_filters.variables,
    (state: RootState) => state.open_data_filters.tiposIndicadores,

    (filterOptions, selectedDerechos, selectedDimensiones, selectedVariables, selectedIndicatorType) => {
        if (!filterOptions) return null;

        const tiposIndicadoresWithDynamicCount = filterOptions.tiposIndicadores.map(tipo => {
            let dynamicCount = 0;

            filterOptions.derechos.forEach(derecho => {
                if (selectedDerechos.length > 0 && !selectedDerechos.includes(derecho.titulo)) {
                    return;
                }

                const tipoEnDerecho = derecho.tipos_indicadores?.find(t => t.titulo === tipo.titulo);
                if (!tipoEnDerecho) return;

                if (selectedDimensiones.length > 0) {
                    const dimensionesEnTipo = tipoEnDerecho.dimensiones?.filter(dim => selectedDimensiones.includes(dim.titulo)) || [];
                    dynamicCount += dimensionesEnTipo.reduce((sum, dim) => sum + dim.count, 0);
                } else {
                    dynamicCount += tipoEnDerecho.count;
                }
            });

            const disabledByCount = dynamicCount === 0;

            const disabledByVariables = selectedVariables.length > 0 && tipo.titulo === INDICATOR_TYPE_STRUCTURAL;

            return {
                ...tipo,
                count: dynamicCount,
                disabled: disabledByCount || disabledByVariables
            };
        });

        const dimensionesWithDisabled = filterOptions.dimensiones.map(dim => {
            const hasMatchingDerecho = dim.derechosTitulos
                ? dim.derechosTitulos.some(d => selectedDerechos.includes(d))
                : true;
            return { ...dim, disabled: !hasMatchingDerecho };
        });

        //Los estructurales no tienen variables relacionadas, por eso se desabilitan las variables
        const isOnlyStructuralSelected = selectedIndicatorType.length === 1 && selectedIndicatorType.includes(INDICATOR_TYPE_STRUCTURAL);

        const variablesWithDisabled = filterOptions.variables.map(variable => ({
            ...variable,
            disabled: isOnlyStructuralSelected
        }));

        return {
            derechos: filterOptions.derechos,
            tiposIndicadores: tiposIndicadoresWithDynamicCount,
            dimensiones: dimensionesWithDisabled,
            ods: filterOptions.ods,
            priorityGroups: filterOptions.priorityGroups,
            variables: variablesWithDisabled
        };
    }
);

export const selectHasFilterChanges = (state: RootState) => {
    const current = state.open_data_filters;
    const initial = state.open_data_filters.initialFilters;
    const filterOptions = state.open_data_filters.filterOptions;

    const isDifferent = (a: string[], b: string[]) =>
        a.length !== b.length || a.some(item => !b.includes(item));

    const allDerechosSelected = filterOptions?.derechos
        ? current.derechos.length === filterOptions.derechos.length
        : false;

    const allDerechosSelectedInitial = filterOptions?.derechos
        ? initial.derechos.length === filterOptions.derechos.length
        : false;

    const normalizedCurrentDerechos = allDerechosSelected ? [] : current.derechos;
    const normalizedInitialDerechos = allDerechosSelectedInitial ? [] : initial.derechos;

    const derechosChanged = isDifferent(normalizedCurrentDerechos, normalizedInitialDerechos);

    const hasChanges = (
        derechosChanged ||
        isDifferent(current.tiposIndicadores, initial.tiposIndicadores) ||
        isDifferent(current.dimensiones, initial.dimensiones) ||
        isDifferent(current.ods, initial.ods) ||
        isDifferent(current.priorityGroups, initial.priorityGroups) ||
        isDifferent(current.variables, initial.variables)
    );

    const allFiltersEmpty =
        normalizedCurrentDerechos.length === 0 &&
        current.tiposIndicadores.length === 0 &&
        current.dimensiones.length === 0 &&
        current.ods.length === 0 &&
        current.priorityGroups.length === 0 &&
        current.variables.length === 0;

    return hasChanges && !allFiltersEmpty;
};

export const selectCanClearFilters = (state: RootState) => {
    const hasApplied = state.open_data_filters.hasAppliedFilters;
    const hasChanges = selectHasFilterChanges(state);

    return hasApplied || hasChanges;
};